import type { ChannelClient } from '@hibro/platform-core';
import {
  type CommandRequestSource,
  detachedSurfaceKey,
  type IframeBridge,
  type InAppNotificationBridge,
  InlineDetachedHost,
  type InlineDetachedStore,
  type LinkedModulesBridge,
  type LoadUi,
  type LogBridge,
  migratedSurfaceKeys,
  type OpenExternalBridge,
  type OsNotificationBridge,
  Workbench,
} from '@hibro/platform-renderer';
import type {
  DetachedSurfaceInfo,
  IAppLinkService,
  IConfigurationService,
  ILauncherService,
  IModuleService,
  INlsService,
  IOverlayService,
  IPartitionService,
  IPermissionService,
  IProductConfigService,
  ISearchRecentService,
  IThemeService,
  IUpdateService,
  IWhenContextService,
} from '@hibro/shared';
import {
  BROWSER_EDITOR_VIEW_ID,
  externalProcessKey,
  NAVIGATE_EXTERNAL_PREFIX,
} from '@hibro/shared';
import { useEffect, useMemo, useState } from 'react';

import { type PickLocalBundle } from './bundle-bridge';
import { createDetachBridge } from './detach-bridge';
import { EmbeddedPlatformSettings } from './embedded-platform-settings';
import { createExternalBridge } from './external-bridge';
import {
  deliverViewNavigation,
  getNavigationRegistry,
} from './navigation-registry';
import { getViewTitleRegistry } from './view-title-registry';
import { createWindowControlsBridge } from './window-controls-bridge';

export interface WorkbenchHostProps {
  /**
   * How `iframe` views get their gated SDK port (见 9 / 18) — Electron brokers
   * it through the preload, the browser runtime mints it in-page. Injected by
   * the boot sequence so this shell is runtime-agnostic.
   */
  iframeBridge: IframeBridge;
  /**
   * Observability sink for the diagnostics section + crash toast + console mirror
   * (见 15 / §5.16). Electron reaches the main-process sink over the preload IPC
   * bridge; the browser runtime supplies a page-side one. Absent means those three
   * simply do not mount.
   */
  logBridge?: LogBridge;
  /**
   * OS-notification permission bridge (见 18 / §5.15). Only the browser runtime
   * has one — a desktop notification is raised by the main process and needs no
   * page grant — so Electron leaves it absent and the settings section that
   * grants it never mounts.
   */
  osNotificationBridge?: OsNotificationBridge;
  /**
   * In-app notification delivery (见 23), for a runtime whose messages do not
   * arrive on a preload global — the browser, where they come over the worker
   * port. Electron leaves it absent and this host builds one from
   * `window.hibroNotification` instead.
   */
  inAppNotifications?: InAppNotificationBridge;
  /**
   * Load-unpacked dev modules (见 11 调试 / 18). Only the browser runtime injects
   * one: on Electron the same verbs ride on the main-process devtools bridge,
   * which this shell builds below from `window.hibroDevtools`.
   */
  linkedModulesBridge?: LinkedModulesBridge;
  /**
   * Page-side file/directory picker for local module install (见 12 / 18).
   * Injected only by the browser runtime so the click gesture is still live
   * when the picker opens. Desktop leaves this absent and uses the main
   * process dialog via `IBundleService.installLocal`.
   */
  pickLocalBundle?: PickLocalBundle;
  openExternalBridge?: OpenExternalBridge;
  /**
   * Detached surfaces this page shows itself, because its runtime could not open
   * a window for them (见 18 弹窗拦截). Filled by the browser runtime's shell;
   * on Electron it simply stays empty and the floating host renders nothing.
   */
  inlineDetached: InlineDetachedStore;
  client: ChannelClient;
  moduleService: IModuleService;
  configService: IConfigurationService;
  productConfigService: IProductConfigService;
  permissionService: IPermissionService;
  overlayService: IOverlayService;
  themeService: IThemeService;
  nlsService: INlsService;
  launcherService: ILauncherService;
  appLinkService: IAppLinkService;
  searchRecentService: ISearchRecentService;
  updateService: IUpdateService;
  whenContextService: IWhenContextService;
  partitionService: IPartitionService;
  uiLoaders: Map<string, LoadUi>;
}

/**
 * App-shell wrapper that owns the cross-window detached-view set (架构方案 §5.8)
 * and feeds it to the platform `Workbench`. Subscribing here (not in the
 * platform package) keeps platform-renderer free of the preload `hibroWindow`
 * bridge. A detached view is filtered out of the inline layout (true migration)
 * and offered Focus/Attach controls instead.
 */
export function WorkbenchHost({
  iframeBridge,
  logBridge,
  osNotificationBridge,
  inAppNotifications: injectedInAppNotifications,
  linkedModulesBridge,
  pickLocalBundle,
  openExternalBridge,
  inlineDetached,
  client,
  moduleService,
  configService,
  productConfigService,
  permissionService,
  overlayService,
  themeService,
  launcherService,
  appLinkService,
  searchRecentService,
  updateService,
  whenContextService,
  partitionService,
  uiLoaders,
}: WorkbenchHostProps) {
  const [detachedSurfaces, setDetachedSurfaces] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Running external processes by process key (架构方案 §3.1/§5.8): drives the
  // activityBar run-state indicator, fed by the unified surface facade. Keyed per
  // process, not per module, so a module owning several entries reports each.
  const [runningExternal, setRunningExternal] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Modules whose lazy `dist/` is downloading/unpacking (见 12 两阶段下载):
  // the matching activityBar icon pulses in place, fed by the main-process dist state.
  const [distFetching, setDistFetching] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Bridge external-process surfaces (架构方案 §3.1/§5.8): an external module's
  // contributed view renders a control panel (`ExternalSurface`) that drives the
  // process through this shell-built bridge.
  const externalBridge = useMemo(() => createExternalBridge(), []);
  // In-app notifications routed here by the host (见 23), and the click report
  // that lets the host resolve where they go. The browser runtime passes its own
  // (the messages arrive on its worker port); Electron builds one from the preload
  // global, same as every other bridge here.
  const inAppNotifications = useMemo<
    InAppNotificationBridge | undefined
  >(() => {
    if (injectedInAppNotifications) {
      return injectedInAppNotifications;
    }
    const api = window.hibroNotification;
    return api
      ? {
          subscribe: (listener) => api.onInApp(listener),
          reportClick: (tag) => api.reportClick(tag),
        }
      : undefined;
  }, [injectedInAppNotifications]);
  // Console-mirror toggle (见 15): read the persisted diagnostics state once and
  // track live changes broadcast by the main process, so toggling it in Settings
  // mounts/unmounts the DevTools LogConsoleMirror without a reload.
  const [mirrorConsole, setMirrorConsole] = useState(false);
  useEffect(() => {
    const api = window.hibroDevtools;
    if (!api) {
      return;
    }
    let active = true;
    void api
      .getState()
      .then((snapshot) => {
        if (active) {
          setMirrorConsole(snapshot.diagnostics.mirrorConsole);
        }
      })
      .catch(() => undefined);
    const dispose = api.onDiagnosticsChanged((diagnostics) =>
      setMirrorConsole(diagnostics.mirrorConsole),
    );
    return () => {
      active = false;
      dispose();
    };
  }, []);
  // Bridge the frameless window's custom title-bar controls to the main process
  // (架构方案 §5.8); built in the shell so platform-renderer stays free of the
  // preload `hibroWindow` global. On macOS the bridge still reports its platform
  // so the controls defer to native traffic lights.
  const windowControlsBridge = useMemo(() => createWindowControlsBridge(), []);
  // Bridge the detach mechanism (§5.8 通用 Detachable); platform-renderer turns
  // it into `ViewProps.detach`/`detached` for module views. Built in the shell so
  // platform-renderer/ui-client stay free of the preload `hibroWindow` global.
  const detachBridge = useMemo(() => createDetachBridge(client), [client]);
  // Navigation source (见 8): bridges the preload `hibro:view:navigate` fan-out
  // into the platform's NavigationContext so inline-dom views receive locations.
  const navigation = useMemo(() => getNavigationRegistry(), []);
  const viewTitles = useMemo(() => getViewTitleRegistry(), []);
  // Commands requested by main-process native chrome — the tray menu (见 23).
  // Built here for the reason every other preload bridge is: the platform
  // package must not reach for an Electron global. Absent on the web, where
  // there is no chrome outside the page to request anything.
  const commandRequests = useMemo<CommandRequestSource | undefined>(() => {
    const api = window.hibroWindow;
    return api
      ? { subscribe: (listener) => api.onRunCommand(listener) }
      : undefined;
  }, []);
  // A newly created workbench can finish loading before main's one-shot
  // navigation event has a renderer listener. Read the sender-bound browser
  // state once so an already-pending editor page still activates its view.
  useEffect(() => {
    let active = true;
    void window.hibroBrowser
      ?.getState()
      .then((state) => {
        if (
          active &&
          state?.presentation === 'editor' &&
          state.tabs.length > 0
        ) {
          deliverViewNavigation(
            BROWSER_EDITOR_VIEW_ID,
            { source: 'openBrowser' },
            state.slotId,
          );
        }
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        window.hibroLog?.append({
          level: 'warn',
          kind: 'log',
          message: 'Failed to restore the browser editor request',
          error: { message },
        });
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    // The editor-tab placeholder only concerns whole-view `migrate` detaches (a
    // fragment `mirror` window leaves its origin untouched, §5.8). Reduce the
    // detached-surface set to those handles — keyed per instance, so detaching
    // one browser tab does not blank its siblings (§5.7 视图多实例).
    const apply = (surfaces: readonly DetachedSurfaceInfo[]): void => {
      if (active) {
        setDetachedSurfaces(migratedSurfaceKeys(surfaces));
      }
    };
    // Read the current set, then follow the host's broadcast — the same
    // read-then-subscribe idiom the theme and gate use, and re-run per client so
    // a host restart re-syncs (the host re-adopts still-open windows, §5.8).
    void detachBridge.listDetached().then(apply);
    const dispose = detachBridge.onDetachedChange(apply);
    return () => {
      active = false;
      dispose();
    };
  }, [detachBridge]);

  useEffect(() => {
    let active = true;
    const apply = (
      surfaces: { kind: string; moduleId?: string; externalEntry?: string }[],
    ): void => {
      setRunningExternal(
        new Set(
          surfaces
            .filter((s) => s.kind === 'external' && s.moduleId)
            .map((s) =>
              externalProcessKey(s.moduleId as string, s.externalEntry),
            ),
        ),
      );
    };
    void window.hibroSurfaces?.list().then((surfaces) => {
      if (active) {
        apply(surfaces);
      }
    });
    const dispose = window.hibroSurfaces?.onChange(apply);
    return () => {
      active = false;
      dispose?.();
    };
  }, []);

  // Lazy-dist fetch state (见 12 两阶段下载): a module joins the set while its
  // `dist/` downloads/unpacks (`fetching`) and leaves on `ready`/`error`, driving
  // the activityBar in-place pulse.
  useEffect(() => {
    return window.hibroBundle?.onDistState((state) => {
      setDistFetching((prev) => {
        const next = new Set(prev);
        if (state.phase === 'fetching') {
          next.add(state.moduleId);
        } else {
          next.delete(state.moduleId);
        }
        return next;
      });
    });
  }, []);

  return (
    <>
      <Workbench
        client={client}
        moduleService={moduleService}
        productConfigService={productConfigService}
        permissionService={permissionService}
        overlayService={overlayService}
        themeService={themeService}
        uiLoaders={uiLoaders}
        detachedSurfaces={detachedSurfaces}
        iframeBridge={iframeBridge}
        externalBridge={externalBridge}
        navigation={navigation}
        viewTitles={viewTitles}
        commandRequests={commandRequests}
        runningExternalProcesses={runningExternal}
        distFetchingModules={distFetching}
        logBridge={logBridge}
        inAppNotifications={inAppNotifications}
        launcherService={launcherService}
        searchRecentService={searchRecentService}
        updateService={updateService}
        whenContextService={whenContextService}
        partitionService={partitionService}
        openExternalBridge={openExternalBridge}
        mirrorConsole={mirrorConsole}
        windowControlsBridge={windowControlsBridge}
        detachBridge={detachBridge}
        onCloseViewInstance={(viewId, instanceKey, reason) => {
          // Closing the tab of a migrated surface also closes the window it was
          // migrated into (§5.8): leaving the window open with no tab to attach
          // back to would strand it — the placeholder is the only Focus/Attach
          // control there is. Attach first so the page is returned to this
          // window, then release the slot; fire-and-forget would race the move.
          // A partition switch parks the page and keeps its URL (见 22).
          const releaseSlot = (): Promise<void> | undefined => {
            if (viewId !== BROWSER_EDITOR_VIEW_ID) {
              return undefined;
            }
            return reason === 'partition'
              ? window.hibroBrowser?.suspendEditorSlot?.(instanceKey)
              : window.hibroBrowser?.disposeEditorSlot?.(instanceKey);
          };
          if (detachedSurfaces.has(detachedSurfaceKey(viewId, instanceKey))) {
            void detachBridge
              .attach({
                viewId,
                ...(instanceKey ? { instanceKey } : {}),
              })
              .then(releaseSlot)
              .catch((error: unknown) => {
                const message =
                  error instanceof Error ? error.message : String(error);
                window.hibroLog?.append({
                  level: 'error',
                  kind: 'log',
                  message:
                    'Failed to close the detached window with its editor tab',
                  error: { message },
                });
              });
            return;
          }
          void releaseSlot()?.catch((error: unknown) => {
            const message =
              error instanceof Error ? error.message : String(error);
            window.hibroLog?.append({
              level: 'warn',
              kind: 'log',
              message: 'Failed to release browser editor slot',
              error: { message },
            });
          });
        }}
        onActivateExternal={(processKey) => {
          void window.hibroSurfaces?.activate(
            `${NAVIGATE_EXTERNAL_PREFIX}${processKey}`,
          );
        }}
        onOpenWindow={() => {
          void window.hibroWindow?.open();
        }}
        onDetachView={(viewId, moduleId, instanceKey) => {
          // Whole-view detach is the fragment-less `migrate` case of the single
          // detach mechanism (§5.8 通用 Detachable): no separate `detach` API.
          void detachBridge.detachFragment({
            viewId,
            moduleId,
            mode: 'migrate',
            ...(instanceKey ? { instanceKey } : {}),
          });
        }}
        onAttachView={(viewId, instanceKey) => {
          void detachBridge.attach({
            viewId,
            ...(instanceKey ? { instanceKey } : {}),
          });
        }}
        onFocusDetached={(viewId, instanceKey) => {
          void detachBridge.focus({
            viewId,
            ...(instanceKey ? { instanceKey } : {}),
          });
        }}
      />
      {/* Surfaces the runtime could not give a window of their own (见 18): a
          floating frame over the workbench, driven by the same detach registry. */}
      <InlineDetachedHost
        store={inlineDetached}
        moduleService={moduleService}
        uiLoaders={uiLoaders}
        client={client}
        iframeBridge={iframeBridge}
        externalBridge={externalBridge}
        navigation={navigation}
        detachBridge={detachBridge}
        updateService={updateService}
        launcherService={launcherService}
        productConfigService={productConfigService}
        openExternalBridge={openExternalBridge}
        whenContextService={whenContextService}
        developerSettings={
          <EmbeddedPlatformSettings
            client={client}
            configService={configService}
            permissionService={permissionService}
            launcherService={launcherService}
            appLinkService={appLinkService}
            searchRecentService={searchRecentService}
            logBridge={logBridge}
            linkedModulesBridge={linkedModulesBridge}
            osNotificationBridge={osNotificationBridge}
            pickLocalBundle={pickLocalBundle}
          />
        }
        log={(message, data) => {
          window.hibroLog?.append({
            level: 'warn',
            kind: 'log',
            message,
            ...(data ? { data } : {}),
          });
        }}
      />
    </>
  );
}
